export interface PaletteHit {
  id: string;
  label: string;
  subtitle: string | null;
  to: string;
  /**
   * Names the permission that gates this hit's visibility (e.g.
   * 'TENANTS_READ', 'BUSINESS_TYPES_READ'), or 'PAGE' for nav pages that
   * everyone who can see them may re-open. Recents are filtered through this
   * at read time so a role downgrade (or a second admin on the same browser
   * profile) can never surface a display name the current admin can't read.
   */
  perm: string;
}

export const RECENTS_KEY = 'heyroot.admin.palette.recents';
export const RECENTS_MAX = 6;

function isHit(v: unknown): v is PaletteHit {
  if (typeof v !== 'object' || v === null) return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o['id'] === 'string' &&
    typeof o['label'] === 'string' &&
    typeof o['to'] === 'string' &&
    typeof o['perm'] === 'string'
  );
}

/**
 * Recently opened palette results, newest first. V1 serves these from
 * GET /api/v1/search/recent; admin keeps them client-side instead, because a
 * per-admin recents table would be this slice's only migration — paid for a
 * convenience list that is worthless on another device anyway.
 */
export function readRecents(): PaletteHit[] {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isHit).slice(0, RECENTS_MAX);
  } catch {
    return [];
  }
}

export function recordRecent(hit: PaletteHit): void {
  try {
    const next = [hit, ...readRecents().filter((r) => r.to !== hit.to)].slice(0, RECENTS_MAX);
    localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch {
    // Convenience only.
  }
}

/** Clears persisted recents. Called on sign-out so a subsequent admin on the
 * same browser profile never inherits the prior admin's recent hits. */
export function clearRecents(): void {
  try {
    localStorage.removeItem(RECENTS_KEY);
  } catch {
    // Convenience only — a blocked store must not break sign-out.
  }
}
